/* eslint-disable */

import { Injectable } from '@angular/core';
import prune from 'json-prune/JSON.prune.js';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  lockCounter = 0;

  constructor() {
    // https://tc39.github.io/ecma262/#sec-array.prototype.includes
    if (!Array.prototype.includes) {
      Object.defineProperty(Array.prototype, 'includes', {
        value(searchElement, fromIndex) {
          if (this == null) {
            throw new TypeError('"this" is null or not defined');
          }

          // 1. Let O be ? ToObject(this value).
          const o = Object(this);

          // 2. Let len be ? ToLength(? Get(O, "length")).
          const len = o.length >>> 0;

          // 3. If len is 0, return false.
          if (len === 0) {
            return false;
          }

          // 4. Let n be ? ToInteger(fromIndex).
          //    (If fromIndex is undefined, this step produces the value 0.)
          const n = fromIndex | 0;

          // 5. If n ≥ 0, then
          //  a. Let k be n.
          // 6. Else n < 0,
          //  a. Let k be len + n.
          //  b. If k < 0, let k be 0.
          let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);

          function sameValueZero(x, y) {
            return x === y || (typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y));
          }

          // 7. Repeat, while k < len
          while (k < len) {
            // a. Let elementK be the result of ? Get(O, ! ToString(k)).
            // b. If SameValueZero(searchElement, elementK) is true, return true.
            if (sameValueZero(o[k], searchElement)) {
              return true;
            }
            // c. Increase k by 1.
            k++;
          }

          // 8. Return false
          return false;
        }
      });
    }

    // Polyfill from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/includes
    if (!String.prototype.includes) {
      String.prototype.includes = function (search: any, start: number) {
        if (search instanceof RegExp) {
          throw TypeError('first argument must not be a RegExp');
        }
        if (start === undefined) {
          start = 0;
        }
        return this.indexOf(search, start) !== -1;
      };
    }
  }

  createLock(name?) {
    return { deferred: null, locked: false, name };
  }

  waitForLock(lock) {
    if (lock.locked) {
      // console.log('synchronizer blocked ', lock);
      return Promise.reject('Init blocked by js lock');
    } else {
      if (lock.counter === undefined) {
        lock.counter = this.lockCounter++;
      }
      // console.log('synchronizer locking ', lock);
      lock.locked = true;
      return Promise.resolve();
    }
  }

  releaseLock(lock, forwardArgArray?) {
    // console.log('synchronizer releasing ', lock);
    lock.locked = false;
    return Promise.resolve();
  }

  toJson(obj) {
    const printedObjects = new Set();
    try {
      return JSON.stringify(obj, function (key, value) {
        if (typeof value === 'object' && value !== null) {
          // Object properties might be non-enumerable, which seems to be the case with Errors
          //   taken from http://www.damirscorner.com/blog/posts/20171027-ReportingJavaScriptErrorDetailsFromPromises.html
          if (value instanceof Error) {
            const error = {};

            Object.getOwnPropertyNames(value).forEach(function (propertyName) {
              error[propertyName] = value[propertyName];
            });

            return error;
          }

          if (printedObjects.has(value)) {
            return '[Already printed]'; // breaks circular refs, could be non-circular (e.g. sibling)
            //                    } else if (value instanceof Date) {
            //                        // workaround for 'toISOString is not a function' - stripped from dates?
            //                        value = Date.prototype.toISOString.apply(value);
          } else if (key === 'core') {
            // workaround for 'toISOString is not a function'
            //   not sure if it's just the core object causing this, but that did cause it
            value = '[core omitted]';
          }
          printedObjects.add(value);
        }
        return value;
      });
    } catch (e) {
      return 'UtilsProvider.toJson cannot stringify obj (' + e.message + ')';
    }
  }

  pruneOptions = { prunedString: undefined }; // 'silent pruning' - just rm'd
  /*{replacer:function(value, defaultValue, circular){
      if (circular) return '"-circular-"';
      if (value === undefined) return '"-undefined-"';
      if (Array.isArray(value) && value.length > 5) return '"-array('+value.length+')-"';
      return defaultValue;
    }};*/

  toPrunedJson(obj) {
    return prune(obj, this.pruneOptions);
  }

  sumCharCodes(str) {
    let i = 0;
    let sum = 0;
    const len = str.length;
    for (; i < len; i++) {
      sum += str.charCodeAt(i);
    }
    return sum;
  }

  randomFromNumber(seed) {
    if (!seed) {
      seed = new Date().getTime();
    }
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280.0;
  }

  randomFromString(seed) {
    return this.randomFromNumber(this.sumCharCodes(seed));
  }

  subset(obj, keys) {
    const subset = {};
    keys.forEach(function (key) {
      subset[key] = obj[key];
    });
    return subset;
  }

  getPromise(obj, fn, argArray?): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const combinedArgs = [
        function () {
          resolve.apply(this, arguments);
        },
        function () {
          reject.apply(this, arguments);
        }
      ];
      // assumes success and failure fns at end (common cordova plugin pattern)
      // prepending argArray, not creating 3rd array (using unshift.apply) for efficiency - we'll probably use this a lot
      // 	i.e. calls obj.fn(argArray[0], argArray[1], ..., resolverFn, rejecterFn);
      if (argArray) {
        combinedArgs.unshift.apply(combinedArgs, argArray);
      }
      fn.apply(obj, combinedArgs);
    });
  }

  firstSerialPromise(fns: Array<() => Promise<any>>): Promise<any> {
    let i = 0;
    const run = function () {
      return fns[i]().then(
        (results) =>
          //console.log('Serial fn', i, results)
          results,
        (reason) => {
          //console.log('Serial fn', i, reason)
          if (++i < fns.length) {
            return run();
          } else {
            return Promise.reject('All rejected');
          }
        }
      );
    };
    return run();
  }

  // from https://hackernoon.com/functional-javascript-resolving-promises-sequentially-7aac18c4431e
  runPromisesSerially(fns: Array<() => Promise<any>>): Promise<Array<any>> {
    return fns.reduce((promise, fn) => promise.then((result) => fn().then(Array.prototype.concat.bind(result))), Promise.resolve([]));
  }

  isEmpty(obj) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  fromJson(json) {
    return typeof json === 'string' ? JSON.parse(json) : json;
  }

  randomizeArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  // like Array.filter, but with promises
  // promiseFn should resolve to true/false and take an array element
  // returns filtered elements and does not modify array (like Array.filter)
  filterWithPromise(array, promiseFn) {
    const promises = [];
    const filtered = [];
    for (let i = 0; i < array.length; i++) {
      promises.push(promiseFn(array[i]));
    }
    return Promise.all(promises).then((results) => {
      for (let i = 0; i < results.length; i++) {
        if (results[i]) {
          filtered.push(array[i]);
        }
      }
      return filtered;
    });
  }

  throwForNotExactly1Key(obj, keys) {
    let count = 0;
    keys.forEach((key) => {
      if (key in obj) {
        count++;
      }
      if (count > 1) {
        throw ['Exactly 1 of ', this.toJson(keys), ' allowed (', this.toJson(obj), ')'].join('');
      }
    });
    if (count === 0) {
      throw ['Exactly 1 of ', this.toJson(keys), ' expected (', this.toJson(obj), ')'].join('');
    }
  }

  sumIntArray(arr) {
    const add = (a, b) => a + b;
    // use reduce to sum our array
    const sum = arr.reduce(add);
    return sum;
  }
}
